import { Button, Icon, useTheme } from "@outschool/backpack";
import { ClassDetailsNoSectionsQuery } from "@outschool/gql-frontend-generated";
import { faEnvelope } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { addParamsToUrl, onboardingQuizPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import { HelpLink } from "@outschool/ui-components-website";
import React from "react";

// @ts-ignore - JSX import
import MessageModal from "../../MessageModal";

interface AskAQuestionButtonProps {
  activity: ClassDetailsNoSectionsQuery["activity"];
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  buttonSize?: "small" | "medium" | "large";
  useIconOnly?: boolean;
}

export function AskAQuestionButton({
  activity,
  leader,
  buttonSize = "medium",
  useIconOnly = false
}: AskAQuestionButtonProps) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\ctas\\AskAQuestionButton"
  );
  const { enterLoginFlow } = useLoginFlowContext();
  const buttonText = t`Ask a Question`;
  const startIcon = <Icon icon={faEnvelope} />;
  const theme = useTheme();
  const { currentUser, isLoggedIn } = useSession();
  const trackEvent = useTrackEvent();
  const shouldApplyEnhancedLoginFlow = useFeatureFlag(
    "enhanced-enter-login-flow"
  );

  if (isLoggedIn) {
    return (
      <MessageModal
        renderButton={(open: () => void) =>
          useIconOnly ? (
            <Button
              size={buttonSize}
              sx={{
                border: "2px solid",
                borderColor: theme.palette.grey[300],
                borderRadius: 16,
                paddingX: 8,
                paddingY: 4,
                color: "black"
              }}
              onClick={() => {
                trackEvent("class_details_message_teacher_intent");
                open();
              }}
            >
              <Icon icon={faEnvelope} />{" "}
            </Button>
          ) : (
            <Button
              color="neutral"
              size={buttonSize}
              sx={{ width: "100%", color: "black" }}
              onClick={() => {
                trackEvent("class_details_message_teacher_intent");
                open();
              }}
              startIcon={startIcon}
            >
              {buttonText}
            </Button>
          )
        }
        user={currentUser}
        requireLogin={true}
        isLoggedIn={isLoggedIn}
        viewName="messageToLeader"
        heading={t("Ask the teacher")}
        prompt={(close: () => void) => (
          <Trans t={t}>
            For example, ask about class details or logistics. For general
            questions about Outschool, you can{" "}
            <HelpLink sx={{ width: "fit-content" }} onClick={close}>
              contact Outschool directly
            </HelpLink>
            .
          </Trans>
        )}
        showFromAndTo={true}
        subject={"Message about: " + activity?.title}
        recipients={[leader]}
        activityUid={activity?.uid}
        onMessageCreated={() => {
          trackEvent("Messaged Teacher");
        }}
        context={{
          action: "Ask a Question",
          activityTitle: activity?.title,
          subject: "Message about: " + activity?.title
        }}
      />
    );
  } else {
    return (
      <Button
        color="neutral"
        size={buttonSize}
        startIcon={startIcon}
        sx={{ width: "100%", paddingX: 8, flex: "1 1 auto", color: "black" }}
        onClick={() => {
          const location = window.location;
          trackEvent("Clicked Ask a Question", {
            leaderUid: leader.uid
          });

          const redirectUrlAfterLoginFlow = addParamsToUrl(
            location.pathname + location.search,
            { openMB: "messageToLeader" }
          );

          if (shouldApplyEnhancedLoginFlow) {
            enterLoginFlow({
              authTrigger: AuthTrigger.MESSAGE_TEACHER,
              newUrlAfterSignUp: addParamsToUrl(onboardingQuizPath(), {
                messageTeacher: true,
                landingPage: redirectUrlAfterLoginFlow
              }),
              newUrlAfterLogin: redirectUrlAfterLoginFlow,
              shouldNavigateOverwrite: false
            });
          } else {
            enterLoginFlow({
              authTrigger: AuthTrigger.MESSAGE_TEACHER,
              newUrl: redirectUrlAfterLoginFlow
            });
          }
        }}
      >
        {buttonText}
      </Button>
    );
  }
}
